import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { AbstractModel } from '@patsnap/tahiti-core/common/model';
import { type ShareAuthCheckData, type DuplicateModuleData, ShareTaskStatus } from '../../types';
import type { DuplicateService } from '../duplicate-service';
import { DuplicateCreateRepository, DuplicateExecuteRepository } from '../share-tools-repository';
import { RouterService } from '@eureka/core-services/src/vue-router-service';

@injectable()
export class DuplicateServiceImpl extends AbstractModel<DuplicateModuleData> implements DuplicateService {
    private taskGroupId = '';
    private _timer: NodeJS.Timeout | null = null;

    _modelData = {
        loading: false,
        status: ShareTaskStatus.PENDING,
    }

    constructor(
        @inject(DuplicateCreateRepository) private duplicateCreateRepository: DuplicateCreateRepository,
        @inject(DuplicateExecuteRepository) private duplicateExecuteRepository: DuplicateExecuteRepository,
        @inject(RouterService) private readonly _routerService: RouterService
    ) {
        super();
    }

    createShareTask(data: ShareAuthCheckData) {
        this.update((v) => {
            return { ...v, loading: true }
        });
        return this.duplicateCreateRepository.createShareTask(data).then((res) => {
            this.taskGroupId = res;
            return res;
        }).catch(e => {
            this.update((v) => { return { ...v, loading: false, status: ShareTaskStatus.FAILED } });
            throw e;
        });
    }

    openUrl(newDataId: string) {
        const router = this._routerService;
        const currentRoute = router.currentRoute.value;
        const currentParams = { ...currentRoute.params };

        const paramKey = Object.keys(currentParams)[0];
        if (!paramKey) {
            console.warn('No route param found to replace')
            return
        }
        // 更新参数值
        currentParams[paramKey] = newDataId;

        // 替换路由，保持原有的 name 和 query
        router.replace({
            name: currentRoute.name,
            params: currentParams,
            query: currentRoute.query,
        })
    }

    executeShareTask() {
        if ([ShareTaskStatus.SUCCESS, ShareTaskStatus.FAILED].includes(this.modelData.status)) return
        if (!this.taskGroupId) return
        this.update((v) => {
            return { ...v, loading: true }
        });

        return this.duplicateExecuteRepository.executeShareTask(this.taskGroupId)
            .then(response => {
                if (response) {
                    if (response.status === ShareTaskStatus.PENDING) {
                        this._timer && clearTimeout(this._timer);
                        this._timer = setTimeout(() => this.executeShareTask(), 100);
                    } else {
                        this.update((v) => { return { ...v, status: response.status, loading: false } });
                        this.openUrl(response.new_data_id);
                    }
                }
            })
            .catch(e => {
                this.update((v) => { return { ...v, loading: false, status: ShareTaskStatus.FAILED } });
                throw e;
            })
            .finally(() => {
                this.update((v) => { return { ...v, loading: false } });
            });
    }

    async fetchData(data: ShareAuthCheckData) {
        await this.createShareTask(data)
        await this.executeShareTask()
    }
}
